import { useCallback, useEffect, useRef, useState } from "react";

export interface ScrollEdges {
  left: boolean;
  right: boolean;
}

/**
 * Tracks whether a horizontally scrollable element still has content past either edge.
 *
 * macOS renders overlay scrollbars that reserve no layout space and fade out when idle — even
 * an explicit `::-webkit-scrollbar { height: 20px }` measures as 0px — so a table that scrolls
 * sideways gives no hint that it does. Callers render a gradient at whichever edge reports
 * true; that fade is the affordance the scrollbar isn't providing.
 *
 * Pass anything that changes the rendered width in `deps` (row count, column visibility,
 * loading state) so the measurement is retaken when the content changes. Wire the returned
 * `ref` to the scroll container and `onScroll` to its scroll event.
 */
export function useScrollEdges<T extends HTMLElement = HTMLDivElement>(
  deps: unknown[] = []
) {
  const ref = useRef<T>(null);
  const [edges, setEdges] = useState<ScrollEdges>({ left: false, right: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    // The 1px tolerance absorbs sub-pixel rounding at the extremes, which would otherwise
    // leave a fade stuck on when the container is fully scrolled.
    setEdges({
      left: el.scrollLeft > 1,
      right: maxScrollLeft - el.scrollLeft > 1,
    });
  }, []);

  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
    // `deps` is spread deliberately — the caller decides what invalidates the measurement.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update, ...deps]);

  return { ref, edges, onScroll: update };
}
